import { useCallback, useMemo, useRef } from "react"

import type { ConversationsTableResult } from "@/types/tables/ConversationsTable"

import type { ConversationsTableFilters } from "../types"
import { getConversationsSimulationOutputIds } from "../utils/conversationsTableState"

interface ConversationsNavigationSessionState {
  clearSavedState: () => void
  saveBeforeNavigation: (params: {
    clickedRowId: string
    scrollOffset: number
    loadedCursors: (string | undefined)[]
    filters?: ConversationsTableFilters
    selectedKeys?: string[]
    selectedRunIdsBySimulationId?: Record<string, string>
  }) => void
}

interface UseConversationsTableNavigationParams {
  allConversationsData: ConversationsTableResult[]
  buildHref: (path: string) => string
  filters: ConversationsTableFilters
  /** A search or cluster filter is narrowing the rows beyond `filters`. */
  isSearchActive: boolean
  loadedCursors: (string | undefined)[]
  onOpenResult: (simulationOutputId: string) => void
  onTrackOpen: (simulationOutputId: string, openedInNewTab: boolean) => void
  selectedKeys: Set<string>
  selectedRunIdsBySimulationId: Record<string, string>
  sessionState: ConversationsNavigationSessionState
  setNavigationData: (ids: string[], currentId: string) => void
}

export function useConversationsTableNavigation({
  allConversationsData,
  buildHref,
  filters,
  isSearchActive,
  loadedCursors,
  onOpenResult,
  onTrackOpen,
  selectedKeys,
  selectedRunIdsBySimulationId,
  sessionState,
  setNavigationData,
}: UseConversationsTableNavigationParams) {
  const scrollOffsetRef = useRef(0)

  const simulationOutputIds = useMemo(
    () => getConversationsSimulationOutputIds(allConversationsData),
    [allConversationsData],
  )

  const handleScrollPositionChange = useCallback((offset: number) => {
    scrollOffsetRef.current = offset
  }, [])

  // Records an open in this tab: analytics, the prev/next chain, and the list
  // state to restore on return. A new-tab open records it too, since the list
  // the user came from is still this one. Search and cluster filters are not
  // kept across a remount, so a searched list has no state worth restoring.
  const recordOpen = useCallback(
    (simulationOutputId: string, openedInNewTab: boolean) => {
      onTrackOpen(simulationOutputId, openedInNewTab)
      setNavigationData(simulationOutputIds, simulationOutputId)

      if (isSearchActive) {
        sessionState.clearSavedState()
        return
      }
      sessionState.saveBeforeNavigation({
        clickedRowId: simulationOutputId,
        scrollOffset: scrollOffsetRef.current,
        loadedCursors,
        filters,
        selectedKeys:
          selectedKeys.size > 0 ? Array.from(selectedKeys) : undefined,
        selectedRunIdsBySimulationId:
          selectedKeys.size > 0 ? selectedRunIdsBySimulationId : undefined,
      })
    },
    [
      filters,
      isSearchActive,
      loadedCursors,
      onTrackOpen,
      selectedKeys,
      selectedRunIdsBySimulationId,
      sessionState,
      setNavigationData,
      simulationOutputIds,
    ],
  )

  const conversationHref = useCallback(
    (simulationOutputId: string) =>
      buildHref(`/uploaded/results/${simulationOutputId}`),
    [buildHref],
  )

  const openConversationResult = useCallback(
    (simulationOutputId: string) => {
      if (!simulationOutputId) {
        return
      }

      recordOpen(simulationOutputId, false)
      onOpenResult(simulationOutputId)
    },
    [onOpenResult, recordOpen],
  )

  // The table has already opened the result in a new tab.
  const handleRowOpenInNewTab = useCallback(
    (simulationOutputId: string) => {
      if (!simulationOutputId) {
        return
      }

      recordOpen(simulationOutputId, true)
    },
    [recordOpen],
  )

  return {
    conversationHref,
    handleRowClick: openConversationResult,
    handleRowOpenInNewTab,
    handleScrollPositionChange,
    openConversationResult,
  }
}
